import CompletenessIndicator from "@/components/CompletenessIndicator/CompletenessIndicator";
import CatalogIcon from "@/components/icons/chapters/CatalogIcon";
import EmployeesIcon from "@/components/icons/chapters/EmployeesIcon";
import FacilitiesIcon from "@/components/icons/chapters/FacilitiesIcon";
import OrdersIcon from "@/components/icons/chapters/OrdersIcon";
import TransportIcon from "@/components/icons/chapters/TransportIcon";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import { useGetFacilities } from "@/hooks/useFacilities";
import { useOrders } from "@/hooks/useOrders";
import useTrackOnLoad from "@/hooks/useTrackPageview";
import { useGetInboundTransportTotals, useGetOutboundTransportTotals } from "@/hooks/useTransport";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import { useGetBusinessTravel } from "@/pages/employee/business_travel/useBusinessTravel";
import { useGetProductEol } from "@/pages/products/eol/useEol";
import { useGetProductUsageOverview } from "@/pages/products/usage/useUsage";
import { useGetCommute } from "../employee/commute/useCommute";
import { useGetProducts, useGetProductsBom } from "../products/useProducts";
import "./Overview.scss";
import Section from "./Section/Section";
import PackageIcon from "@/components/icons/chapters/PackageIcon";
import { useGetPackagings } from "@/pages/packaging/usePackagings";

export default function Overview() {
  useTrackOnLoad("Overview Viewed");
  const { data: companySettings } = useCompanySettings();
  const { year } = useDashboardYear();

  const productsQuery = useGetProducts({ year, enabled: !!year });
  const bomQuery = useGetProductsBom({ year, enabled: !!year });
  const packagingQuery = useGetPackagings({ year, enabled: !!year });
  const inboundTransportQuery = useGetInboundTransportTotals({ year });
  const outboundTransportQuery = useGetOutboundTransportTotals({
    year,
    enabled: !!companySettings && !!year,
    byTransportCategory: companySettings?.transport_show_by_category || false,
  });

  const facilitiesQuery = useGetFacilities({ year, enabled: !!year });
  const salesOrdersQuery = useOrders({ orderType: "sales", year, enabled: !!year });
  const purchaseOrdersQuery = useOrders({ orderType: "purchase", year, enabled: !!year });

  const eolQuery = useGetProductEol({ year, enabled: !!year });
  const usageQuery = useGetProductUsageOverview({ year, enabled: !!year });

  const commuteQuery = useGetCommute({ year, enabled: !!year });
  const businessTravelQuery = useGetBusinessTravel({ year, enabled: !!year });

  const includeEol = companySettings?.include_eol || false;

  const includeUsage = companySettings?.include_usage || false;

  const eolStat = includeEol
    ? [
        {
          name: "End of life",
          description:
            "Information about disposal, recycling, or reuse of sold products after their use.",
          query: eolQuery,
          to: "/products/eol",
        },
      ]
    : [];

  const usageStat = includeUsage
    ? [
        {
          name: "Usage",
          description:
            "Information on how customers use products, like lifespan and resources used.",
          query: usageQuery,
          to: "/products/usage",
        },
      ]
    : [];

  return (
    <div className="Home">
      <PageHeader title={"Overview"} />
      <div className="pickers">
        <YearSelector />
        <CompletenessIndicator type={"collecting"} />
      </div>
      <div className="sections">
        <Section
          name={"Orders"}
          description={"An overview of all your order activities."}
          icon={
            <OrdersIcon
              width={18}
              height={18}
            />
          }
          stats={[
            {
              name: "Purchase orders",
              description: "Details of products ordered from your suppliers.",
              query: purchaseOrdersQuery,
              to: "/orders/purchase_orders",
            },
            {
              name: "Sales orders",
              description: "Details of products sold to your customers.",
              query: salesOrdersQuery,
              to: "/orders/sales_orders",
            },
          ]}
        />
        <Section
          name={"Products"}
          description={
            "A complete list of your products and materials, along with data on their use and disposal."
          }
          icon={
            <CatalogIcon
              width={18}
              height={14}
            />
          }
          stats={[
            {
              name: "Overview",
              description: "Finished products your company sells or produces for customers.",
              query: productsQuery,
              to: "/products",
            },
            {
              name: "Bill of materials",
              description: "Raw materials and components used to make your products.",
              query: bomQuery,
              to: "/products/bom",
            },
            ...usageStat,
            ...eolStat,
          ]}
        />

        <Section
          name={"Product packaging"}
          description={"A complete list of your product packaging."}
          icon={
            <PackageIcon
              width={18}
              height={14}
            />
          }
          stats={[
            {
              name: "Bill of materials",
              description: "Raw materials and components used to make your packaging.",
              query: packagingQuery,
              to: "/packaging",
            },
          ]}
        />

        <Section
          name={"Locations"}
          description={
            "All physical facilities where your company operates, such as offices, warehouses, or factories."
          }
          icon={
            <FacilitiesIcon
              width={18}
              height={16}
            />
          }
          stats={[
            {
              name: "Facilities",
              description:
                "Buildings or factories where production, storage, or other business activities take place.",
              query: facilitiesQuery,
              to: "/facilities",
            },
          ]}
        />

        <Section
          name={"Transport"}
          description={
            "The movement of products between locations across your supply chain, including transport modes and legs."
          }
          icon={
            <TransportIcon
              width={16}
              height={16}
            />
          }
          stats={[
            {
              name: "Inbound",
              description: "Transport data on incoming orders and products from suppliers.",
              query: inboundTransportQuery,
              notAvailableMessage:
                purchaseOrdersQuery.data?.total_count === 0
                  ? "Upload purchase orders in order to start with inbound transport data."
                  : undefined,
              to: "/transport/inbound",
            },
            {
              name: "Outbound",
              description: "Transport data on outgoing orders and products to customers or stores.",
              query: outboundTransportQuery,
              notAvailableMessage:
                salesOrdersQuery.data?.total_count === 0
                  ? "Upload sales orders in order to start with outbound transport data."
                  : undefined,
              to: "/transport/outbound",
            },
          ]}
        />

        <Section
          name={"Employees"}
          description={"All people working for your company."}
          icon={
            <EmployeesIcon
              width={16}
              height={16}
            />
          }
          stats={[
            {
              name: "Commute",
              description: "Travel by employees between their homes and workplace.",
              query: commuteQuery,
              notAvailableMessage:
                purchaseOrdersQuery.data?.total_count === 0 &&
                salesOrdersQuery.data?.total_count === 0
                  ? "Upload purchase orders in order to start with employees transport data."
                  : undefined,
              to: "/employees/commute",
            },
            {
              name: "Business travel",
              description: "Employee travel for work-related purposes beyond their usual commute.",
              query: businessTravelQuery,
              notAvailableMessage:
                purchaseOrdersQuery.data?.total_count === 0 &&
                salesOrdersQuery.data?.total_count === 0
                  ? "Upload purchase orders in order to start with employees transport data."
                  : undefined,
              to: "/employees/business_travel",
            },
          ]}
        />
      </div>
    </div>
  );
}
